import { useCachedActions } from '@hooks/useCachedActions';
import type { FetchData } from '@hooks/useDataFetcher';
import { buildCacheKey, toQueryStringValue } from '@shared/util/cache-utils';
import { useCallback, useMemo, useState } from 'react';
import type {
  IOffersV2CreateOfferPayload,
  IOffersV2Offer,
  IOffersV2OfferStatus,
  IOffersV2VehicleGroup,
} from 'types/custom';
import type {
  ListResponseOfOffer,
  ObjectResponseOfOffer,
  ObjectResponseOfOfferGroupResponse,
  OfferGroupResponse,
} from 'types/offers-v2';

import type { SnackbarActions } from './useSnackbar';

type OffersResult = { list: IOffersV2Offer[]; totalCount: number };

interface IActions {
  getOfferGroup: (groupId: string) => Promise<OfferGroupResponse | undefined>;
  getOfferById: (offerId: string) => Promise<IOffersV2Offer | undefined>;
  getVehicleGroups: (market: string) => Promise<IOffersV2VehicleGroup[]>;
  createOffer: (
    payload: IOffersV2CreateOfferPayload,
  ) => Promise<OfferGroupResponse | undefined>;
  updateOfferGroup: (
    groupId: string,
    payload: IOffersV2CreateOfferPayload,
  ) => Promise<OfferGroupResponse | undefined>;
  updateOfferStatus: (
    offerId: string,
    status: IOffersV2OfferStatus,
    market: string,
    reason?: string,
  ) => Promise<boolean>;
  getOffers: (
    market: string,
    status: IOffersV2OfferStatus,
    variantRef?: string,
    productId?: string,
    modelYear?: number | string,
    offset?: number | string,
    limit?: number | string,
  ) => Promise<OffersResult>;
}

export type UseOffersV2 = (
  fetchData: FetchData,
  snackbar: SnackbarActions,
) => [{ loading: boolean }, IActions];

export const useOffersV2: UseOffersV2 = (makeRequest, snackbar) => {
  const [fetchCount, setFetchCount] = useState(0);
  const { loading, createAction, invalidateCache } = useCachedActions(snackbar);

  const getOfferGroup = useCallback<IActions['getOfferGroup']>(
    async (groupId) => {
      try {
        const response = await makeRequest<ObjectResponseOfOfferGroupResponse>(
          `/api/offers-v2/offer-groups/${encodeURIComponent(groupId)}`,
        );
        if (
          response?.data?.id !== groupId ||
          !Array.isArray(response.data.offers)
        ) {
          throw new Error(
            'Malformed response: expected an offer group with offers.',
          );
        }
        return response.data;
      } catch (error: unknown) {
        snackbar.error(
          error instanceof Error ? error.message : 'Failed to load offer group',
        );
        return undefined;
      }
    },
    [makeRequest, snackbar],
  );

  const createOffer = useCallback<IActions['createOffer']>(
    async (payload) => {
      setFetchCount((c) => c + 1);

      try {
        const response = await makeRequest<ObjectResponseOfOfferGroupResponse>(
          '/api/offers-v2/offer-groups',
          undefined,
          'POST',
          payload,
        );

        invalidateCache(
          (key) =>
            key.startsWith('getOffers|') &&
            key.split('|').includes(`market=${payload.market}`),
        );

        return response?.data;
      } catch (err: unknown) {
        snackbar.error(
          err instanceof Error ? err.message : 'Failed to create offer',
        );
      } finally {
        setFetchCount((c) => c - 1);
      }
    },
    [invalidateCache, makeRequest, snackbar],
  );

  const updateOfferGroup = useCallback<IActions['updateOfferGroup']>(
    async (groupId, payload) => {
      setFetchCount((c) => c + 1);

      try {
        const response = await makeRequest<ObjectResponseOfOfferGroupResponse>(
          `/api/offers-v2/offer-groups/${encodeURIComponent(groupId)}`,
          undefined,
          'PUT',
          payload,
        );

        invalidateCache(
          (key) =>
            key.startsWith('getOffers|') &&
            key.split('|').includes(`market=${payload.market}`),
        );
        // A group edit can change, add, discard or archive any Offer of the group.
        invalidateCache((key) => key.startsWith('getOfferById|'));

        return response?.data;
      } catch (err: unknown) {
        snackbar.error(
          err instanceof Error ? err.message : 'Failed to update offer group',
        );
      } finally {
        setFetchCount((c) => c - 1);
      }
    },
    [invalidateCache, makeRequest, snackbar],
  );

  const updateOfferStatus = useCallback<IActions['updateOfferStatus']>(
    async (offerId, status, market, reason) => {
      setFetchCount((c) => c + 1);

      try {
        await makeRequest(
          `/api/offers-v2/offers/${offerId}/${status}`,
          undefined,
          'POST',
          { reason },
        );

        // status change moves the offer between cached status buckets, so invalidate the whole market
        invalidateCache(
          (key) =>
            key.startsWith('getOffers|') &&
            key.split('|').includes(`market=${market}`),
        );
        invalidateCache(
          (key) => key === buildCacheKey('getOfferById', { offerId }),
        );

        return true;
      } catch (err: unknown) {
        snackbar.error(
          err instanceof Error ? err.message : 'Failed to update offer status',
        );

        return false;
      } finally {
        setFetchCount((c) => c - 1);
      }
    },
    [invalidateCache, makeRequest, snackbar],
  );

  const actions = useMemo<IActions>(
    () => ({
      getOfferGroup,
      createOffer,
      updateOfferGroup,
      updateOfferStatus,
      getOfferById: createAction(
        (offerId) => buildCacheKey('getOfferById', { offerId }),
        async (offerId): Promise<IOffersV2Offer> => {
          const response = await makeRequest<ObjectResponseOfOffer>(
            `/api/offers-v2/offers/${offerId}`,
          );

          if (!response?.data?.id) {
            throw new Error(
              'Malformed response: expected an offer with an id.',
            );
          }

          return response.data;
        },
        undefined,
      ),
      getVehicleGroups: createAction(
        (market) => buildCacheKey('getVehicleGroups', { market }),
        async (market: string): Promise<IOffersV2VehicleGroup[]> => {
          const response = await makeRequest<{
            data?: IOffersV2VehicleGroup[];
          }>('/api/offers-v2/products/vehicle-groups', { market });

          if (response?.data !== undefined && !Array.isArray(response.data)) {
            throw new Error(
              'Malformed response: expected data to be an array.',
            );
          }

          return response?.data ?? [];
        },
        [],
      ),
      getOffers: createAction(
        (market, status, variantRef, productId, modelYear, offset, limit) =>
          buildCacheKey('getOffers', {
            limit,
            status,
            market,
            offset,
            modelYear,
            productId,
            variantRef,
          }),
        async (
          market,
          status,
          variantRef,
          productId,
          modelYear,
          offset,
          limit,
        ): Promise<OffersResult> => {
          const response = await makeRequest<ListResponseOfOffer>(
            '/api/offers-v2/offers',
            {
              limit: toQueryStringValue(limit),
              market: toQueryStringValue(market),
              offset: toQueryStringValue(offset),
              status: toQueryStringValue(status),
              productId: toQueryStringValue(productId),
              modelYear: toQueryStringValue(modelYear),
              variantRef: toQueryStringValue(variantRef),
            },
          );

          if (response?.data !== undefined && !Array.isArray(response.data)) {
            throw new Error(
              'Malformed response: expected data to be an array.',
            );
          }

          const offers = response?.data ?? [];
          return {
            list: offers,
            totalCount: Number(response?.pagination?.total ?? offers.length),
          };
        },
        { list: [], totalCount: 0 },
      ),
    }),
    [
      createAction,
      createOffer,
      getOfferGroup,
      makeRequest,
      updateOfferGroup,
      updateOfferStatus,
    ],
  );

  return [{ loading: loading || fetchCount > 0 }, actions];
};
